import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

interface PracticeProblem {
  problemId: string;
  problemNumber: number;
  problemStatement: string;
  answerChoices: {
    A?: string;
    B?: string;
    C?: string;
    D?: string;
    E?: string;
  };
  correctAnswer: string;
  difficulty: string;
  mainTopic: string;
  subtopic: string;
  relatedConcepts: string[];
  aopsSourceLink?: string;
  problemImage?: string;
  contest: {
    year: number;
    contestType: string;
    version: string;
    name: string;
  };
  originalAttemptDate: string;
  userPreviousAnswer: string;
}

interface PracticeSession {
  topic: string;
  subtopic: string;
  totalProblems: number;
  problems: PracticeProblem[];
}

interface PracticeResult {
  isCorrect: boolean;
  correctAnswer: string;
  practiceResolved: boolean;
  explanation: {
    difficulty: string;
    mainTopic: string;
    subtopic: string;
    relatedConcepts: string[];
    aopsSourceLink?: string;
  };
}

// Helper function to format mathematical expressions (reused from AMCTestInterface)
const formatMathExpression = (text: string): string => {
  let result = text;
  
  // Convert Unicode superscripts to regular format before processing
  result = result.replace(/(\d)([²³⁴⁵⁶⁷⁸⁹⁰¹]+)/g, (match, base, sup) => {
    const superscriptMap: {[key: string]: string} = {'²':'2','³':'3','⁴':'4','⁵':'5','⁶':'6','⁷':'7','⁸':'8','⁹':'9','⁰':'0','¹':'1'};
    const exponent = sup.split('').map((c: string) => superscriptMap[c] || c).join('');
    return `${base}^{${exponent}}`;
  });
  
  // Convert Unicode subscripts to LaTeX subscripts
  result = result.replace(/([a-zA-Z])([₀₁₂₃₄₅₆₇₈₉ₖ]+)/g, (match, base, sub) => {
    const subscriptMap: {[key: string]: string} = {'ₖ':'k','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9','₀':'0'};
    const subscript = sub.split('').map((c: string) => subscriptMap[c] || c).join('');
    return `${base}_{${subscript}}`;
  });
  
  return result
    .replace(/\(([^)]+)\)\/\(([^)]+)\)/g, (match, num, den) => {
      const processedNum = num.replace(/(\d+)\^(\d+)/g, '$1^{$2}');
      const processedDen = den.replace(/(\d+)\^(\d+)/g, '$1^{$2}');
      return `\\frac{${processedNum}}{${processedDen}}`;
    })
    .replace(/⌊(\d+)\/([a-zA-Z])⌋/g, '\\lfloor\\frac{$1}{$2}\\rfloor')
    .replace(/(\d+)\/([a-zA-Z])/g, '\\frac{$1}{$2}')
    .replace(/([a-zA-Z])\/(\d+)/g, '\\frac{$1}{$2}')
    .replace(/([a-zA-Z])\/([a-zA-Z])/g, '\\frac{$1}{$2}')
    .replace(/(\d+)\/(\d+\^\{[^}]+\})/g, '\\frac{$1}{$2}')
    .replace(/(\d+)\/(\d+!)/g, '\\frac{$1}{$2}')
    .replace(/(\d+)\/(\d+)/g, '\\frac{$1}{$2}')
    .replace(/(\d+)\^(\d+)/g, '$1^{$2}')
    .replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')
    .replace(/√(\d+)/g, '\\sqrt{$1}')
    .replace(/≤/g, '\\leq')
    .replace(/≥/g, '\\geq')
    .replace(/≠/g, '\\neq')
    .replace(/∞/g, '\\infty')
    .replace(/π/g, '\\pi')
    .replace(/°/g, '^\\circ');
};

// Helper function to render mathematical content (reused from AMCTestInterface)
const renderMathContent = (content: string) => {
  if (!content) return null;
  
  const lines = content.split('\n');
  
  return lines.map((line, index) => {
    // Check for set notation like {1, 2, 3, ..., 10}
    if (line.match(/\{[^}]*\}/)) {
      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {line.split(/(\{[^}]*\})/).map((part, i) => {
            if (part.match(/^\{[^}]*\}$/)) {
              const mathPart = part.replace(/\{([^}]*)\}/, '\\{$1\\}').replace(/\.\.\./g, '\\ldots');
              return <InlineMath key={i} math={mathPart} />;
            }
            return part;
          })}
        </div>
      );
    }
    
    // Check for complex math expressions
    if (line.match(/[a-zA-Z][²₁₂₃]|[a-zA-Z]\^?\d|√\d/)) {
      const parts = line.split(/(\s+)/);
      return (
        <div key={index} style={{ margin: '5px 0' }}>
          {parts.map((part, i) => {
            if (part.match(/^\s+$/)) return part;
            
            if (part.match(/[a-zA-Z][²₁₂₃]|[a-zA-Z]\^?\d|√\d/)) {
              const mathPart = formatMathExpression(part);
              return <InlineMath key={i} math={mathPart} />;
            }
            return part;
          })}
        </div>
      );
    }
    
    // Regular text
    return (
      <div key={index} style={{ margin: '5px 0' }}>
        {line}
      </div>
    );
  });
};

const AMCMissedPractice: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useUser();
  
  const topic = searchParams.get('topic');
  const subtopic = searchParams.get('subtopic');
  
  const [practiceSession, setPracticeSession] = useState<PracticeSession | null>(null);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [practiceResult, setPracticeResult] = useState<PracticeResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedProblems, setCompletedProblems] = useState<number[]>([]);

  useEffect(() => {
    if (token && topic && subtopic) {
      fetchPracticeProblems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, topic, subtopic]);

  const fetchPracticeProblems = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/amc/practice-missed?topic=${encodeURIComponent(topic!)}&subtopic=${encodeURIComponent(subtopic!)}`);
      
      if (response.success) {
        setPracticeSession(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch practice problems');
      }
    } catch (err) {
      console.error('Error fetching practice problems:', err);
      setError(err instanceof Error ? err.message : 'Failed to load practice problems');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!selectedAnswer || !practiceSession) return;

    const currentProblem = practiceSession.problems[currentProblemIndex];
    
    try {
      setSubmitting(true);
      const response = await apiService.post('/amc/practice-missed/submit', {
        problemId: currentProblem.problemId,
        answerGiven: selectedAnswer
      });

      if (response.success) {
        setPracticeResult(response.result);
        setHasSubmitted(true);
        
        if (response.result.isCorrect) {
          setCompletedProblems(prev => {
            if (!prev.includes(currentProblemIndex)) {
              return prev.concat(currentProblemIndex);
            }
            return prev;
          });
        }
      } else {
        throw new Error(response.message || 'Failed to submit answer');
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const goToNextProblem = () => {
    if (currentProblemIndex < practiceSession!.problems.length - 1) {
      setCurrentProblemIndex(currentProblemIndex + 1);
      setSelectedAnswer('');
      setHasSubmitted(false);
      setPracticeResult(null);
    }
  };

  const goToPreviousProblem = () => {
    if (currentProblemIndex > 0) {
      setCurrentProblemIndex(currentProblemIndex - 1);
      setSelectedAnswer('');
      setHasSubmitted(false);
      setPracticeResult(null);
    }
  };

  const goBackToMissedQuestions = () => {
    navigate('/amc-missed-questions');
  };

  if (loading) {
    return (
      <>
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '18px', color: '#64748b' }}>Loading practice problems...</div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px'
          }}>
            <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Error</h2>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>{error}</p>
            <button onClick={goBackToMissedQuestions} style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              ← Back to Missed Questions
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!practiceSession || practiceSession.problems.length === 0) {
    return (
      <>
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '16px'
            }}>All Done!</h2>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>
              You don't have any missed problems in <strong>{topic} - {subtopic}</strong> to practice.
            </p>
            <button onClick={goBackToMissedQuestions} style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              ← Back to Missed Questions
            </button>
          </div>
        </div>
      </>
    );
  }

  const currentProblem = practiceSession.problems[currentProblemIndex];
  const isLastProblem = currentProblemIndex === practiceSession.problems.length - 1;

  return (
    <>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #1e293b, #374151)',
            color: 'white',
            padding: '20px 30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: '700', margin: '0 0 4px 0' }}>
                Practice: {topic} - {subtopic}
              </h1>
              <p style={{ fontSize: '14px', opacity: 0.8, margin: 0 }}>
                Problem {currentProblemIndex + 1} of {practiceSession.problems.length} • {completedProblems.length} completed
              </p>
            </div>
            <button onClick={goBackToMissedQuestions} style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ChevronLeft size={16} />
              Back
            </button>
          </div>

          {/* Progress Bar */}
          <div style={{
            background: '#f1f5f9',
            height: '4px',
            position: 'relative'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              height: '100%',
              width: `${((currentProblemIndex + 1) / practiceSession.problems.length) * 100}%`,
              transition: 'width 0.3s ease'
            }} />
          </div>

          {/* Problem Content */}
          <div style={{ padding: '30px' }}>
            {/* Problem Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px',
              padding: '16px 20px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  Problem #{currentProblem.problemNumber}
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>
                  {currentProblem.contest.name} • {currentProblem.difficulty}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Originally missed: {new Date(currentProblem.originalAttemptDate).toLocaleDateString()}
                </div>
              </div>
              {completedProblems.includes(currentProblemIndex) && (
                <div style={{
                  background: '#10b981',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <CheckCircle size={14} />
                  Resolved
                </div>
              )}
            </div>

            {/* Problem Statement */}
            <div style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: '#1e293b',
              marginBottom: '32px',
              padding: '24px',
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              {renderMathContent(currentProblem.problemStatement)}
            </div>

            {/* Answer Choices */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Answer Choices:
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(currentProblem.answerChoices).map(([letter, choice]) => (
                  <button
                    key={letter}
                    onClick={() => !hasSubmitted && setSelectedAnswer(letter)}
                    disabled={hasSubmitted}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '16px 20px',
                      background: selectedAnswer === letter 
                        ? (hasSubmitted 
                          ? (practiceResult?.isCorrect && selectedAnswer === letter 
                            ? '#dcfdf7' 
                            : (selectedAnswer === letter ? '#fef2f2' : '#ffffff'))
                          : '#eff6ff')
                        : (hasSubmitted && letter === practiceResult?.correctAnswer 
                          ? '#dcfdf7' 
                          : '#ffffff'),
                      border: selectedAnswer === letter 
                        ? (hasSubmitted 
                          ? (practiceResult?.isCorrect && selectedAnswer === letter 
                            ? '2px solid #10b981' 
                            : (selectedAnswer === letter ? '2px solid #ef4444' : '1px solid #e2e8f0'))
                          : '2px solid #3F8EF6')
                        : (hasSubmitted && letter === practiceResult?.correctAnswer 
                          ? '2px solid #10b981' 
                          : '1px solid #e2e8f0'),
                      borderRadius: '12px',
                      cursor: hasSubmitted ? 'default' : 'pointer',
                      textAlign: 'left',
                      fontSize: '16px',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: selectedAnswer === letter 
                        ? (hasSubmitted 
                          ? (practiceResult?.isCorrect && selectedAnswer === letter 
                            ? '#10b981' 
                            : (selectedAnswer === letter ? '#ef4444' : '#64748b'))
                          : '#3F8EF6')
                        : (hasSubmitted && letter === practiceResult?.correctAnswer 
                          ? '#10b981' 
                          : '#64748b'),
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      fontSize: '14px',
                      flexShrink: 0
                    }}>
                      {letter}
                    </div>
                    <div style={{ flex: 1, paddingTop: '6px' }}>
                      {renderMathContent(choice!)}
                    </div>
                    {hasSubmitted && (
                      <div style={{ flexShrink: 0, paddingTop: '4px' }}>
                        {practiceResult?.isCorrect && selectedAnswer === letter && (
                          <CheckCircle size={20} style={{ color: '#10b981' }} />
                        )}
                        {!practiceResult?.isCorrect && selectedAnswer === letter && (
                          <XCircle size={20} style={{ color: '#ef4444' }} />
                        )}
                        {letter === practiceResult?.correctAnswer && selectedAnswer !== letter && (
                          <CheckCircle size={20} style={{ color: '#10b981' }} />
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>


            {/* Submit Button */}
            {!hasSubmitted && (
              <button
                onClick={submitAnswer}
                disabled={!selectedAnswer || submitting}
                style={{
                  width: '100%',
                  background: selectedAnswer && !submitting 
                    ? 'linear-gradient(135deg, #3F8EF6, #A0F0D0)' 
                    : '#e2e8f0',
                  color: selectedAnswer && !submitting ? 'white' : '#94a3b8',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: '600',
                  cursor: selectedAnswer && !submitting ? 'pointer' : 'not-allowed',
                  marginBottom: '24px',
                  transition: 'all 0.2s ease'
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Answer'}
              </button>
            )}

            {/* Result and Explanation */}
            {hasSubmitted && practiceResult && (
              <div style={{
                background: practiceResult.isCorrect ? '#dcfdf7' : '#fef2f2',
                border: `1px solid ${practiceResult.isCorrect ? '#10b981' : '#ef4444'}`,
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  {practiceResult.isCorrect ? (
                    <CheckCircle size={24} style={{ color: '#10b981' }} />
                  ) : (
                    <XCircle size={24} style={{ color: '#ef4444' }} />
                  )}
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: practiceResult.isCorrect ? '#065f46' : '#991b1b',
                    margin: 0
                  }}>
                    {practiceResult.isCorrect ? 'Correct!' : 'Incorrect'}
                  </h3>
                </div>

                <div style={{
                  fontSize: '16px',
                  color: practiceResult.isCorrect ? '#065f46' : '#991b1b',
                  marginBottom: '16px'
                }}>
                  {practiceResult.isCorrect 
                    ? 'Great job! You\'ve resolved this missed problem.' 
                    : `The correct answer is ${practiceResult.correctAnswer}.`}
                </div>

                {/* Concepts and Resources */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '16px'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Related Concepts:
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {practiceResult.explanation.relatedConcepts && practiceResult.explanation.relatedConcepts.length > 0 ? (
                      practiceResult.explanation.relatedConcepts.map((concept, index) => (
                        <span key={index} style={{
                          background: '#3F8EF6',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {concept}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#64748b', fontSize: '14px', fontStyle: 'italic' }}>
                        No related concepts available
                      </span>
                    )}
                  </div>
                </div>

                {/* View Solution Button - Separate from concepts */}
                {practiceResult.explanation.aopsSourceLink && (
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <a
                      href={practiceResult.explanation.aopsSourceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                        color: 'white',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '16px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(63, 142, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                      View Solution
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={goToPreviousProblem}
                disabled={currentProblemIndex === 0}
                style={{
                  background: currentProblemIndex > 0 ? '#f8fafc' : '#e2e8f0',
                  color: currentProblemIndex > 0 ? '#1e293b' : '#94a3b8',
                  border: '1px solid #e2e8f0',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: currentProblemIndex > 0 ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              {!isLastProblem && hasSubmitted && (
                <button
                  onClick={goToNextProblem}
                  style={{
                    background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              )}

              {isLastProblem && hasSubmitted && (
                <button
                  onClick={goBackToMissedQuestions}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <CheckCircle size={16} />
                  Finish Practice
                </button>
              )}
            </div>

            {/* Practice Again Button */}
            {hasSubmitted && !practiceResult?.isCorrect && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button
                  onClick={() => {
                    setSelectedAnswer('');
                    setHasSubmitted(false);
                    setPracticeResult(null);
                  }}
                  style={{
                    background: 'transparent',
                    color: '#3F8EF6',
                    border: '1px solid #3F8EF6',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  <RotateCcw size={14} />
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AMCMissedPractice;